// src/pages/Institution/institutionThunk.js
import { createAsyncThunk } from "@reduxjs/toolkit";
import {
  getInstitutionApi,
  saveInstitutionApi,
  getCountDetailsApi,
  singleInstitutionDetailApi,
  updateOnboardingStatusApi,
} from "../../../apiservice/setting";
import dayjs from "dayjs";

const clean = (v, maxLen = null) => {
  if (v == null) return "";
  let str = String(v).trim();
  if (maxLen && str.length > maxLen) str = str.substring(0, maxLen);
  return str;
};
const cleanNum = (v, fallback = 0) => {
  const n = Number(v);
  return isNaN(n) ? fallback : n;
};
const safeDate = (val) => (val ? dayjs(val).format("DD/MM/YYYY") : null);

// 🔹 Append only if value is non-empty
const appendIfNotEmpty = (formData, key, value) => {
  if (
    value !== undefined &&
    value !== null &&
    String(value).trim() !== ""
  ) {
    formData.append(key, value);
  }
};

const buildInstitutionFormData = (values) => {

  const formData = new FormData();

  appendIfNotEmpty(formData, "name", clean(values.companyName, 100));
  // appendIfNotEmpty(formData, "company_title", clean(values.legalName, 100));
  appendIfNotEmpty(formData, "company_email", clean(values.companyEmail, 40));
  formData.append("country_code", clean(values.countryCode, 20));
  appendIfNotEmpty(formData, "phone_no", clean(values.phoneNo, 20));
  appendIfNotEmpty(formData, "country", clean(values.country, 50));
  appendIfNotEmpty(formData, "description", clean(values.description, 60));
  appendIfNotEmpty(formData, "currency", clean(values.currency, 10));
  appendIfNotEmpty(formData, "tax_form", clean(values.taxForm, 20));
  appendIfNotEmpty(formData, "ein_ssn", clean(values.einSsn, 10));
  appendIfNotEmpty(formData, "vat_license", clean(values.vatLicense, 20));
  appendIfNotEmpty(formData, "trade_license_no", clean(values.tradeLicenseNo, 20));

  appendIfNotEmpty(formData, "gln_no", clean(values.glnNo, 30));
  appendIfNotEmpty(formData, "item_tax", cleanNum(values.vatPercent, 0));
 
  if (values.dhaMohNo != 0) {
    appendIfNotEmpty(formData, "dha_moh_no", clean(values.dhaMohNo, 20));
  }
  // ✅ Only append if valid date
  if (values.tradeLicenseExpiry && dayjs(values.tradeLicenseExpiry).isValid()) {
    formData.append(
      "trade_license_expiry",
      dayjs(values.tradeLicenseExpiry).format("YYYY-MM-DD")
    );
  }
  // Add this alongside the other date fields in buildInstitutionFormData:

  if (values.migration_date && dayjs(values.migration_date).isValid()) {
    formData.append(
      "migration_date",
      dayjs(values.migration_date).format("YYYY-MM-DD")
    );
  }
  if (values.subscriptionExpiry && dayjs(values.subscriptionExpiry).isValid()) {
    formData.append(
      "subscription_expiry",
      dayjs(values.subscriptionExpiry).format("YYYY-MM-DD")
    );
  }

  if (values.dhaMohExpiry && dayjs(values.dhaMohExpiry).isValid()) {
    formData.append(
      "dha_moh_expiry",
      dayjs(values.dhaMohExpiry).format("YYYY-MM-DD")
    );
  }

  if (values.glnExpiry && dayjs(values.glnExpiry).isValid()) {
    formData.append(
      "gln_no_expiry",
      dayjs(values.glnExpiry).format("YYYY-MM-DD")
    );
  }
  formData.append(
    "fy_start_month",
    values.fy_start_month || values.fyStartMonth || 1
  );
  formData.append("street_address", values.streetAddress);
  formData.append("company_type", values.companyType || undefined);
  formData.append("company_title", values.legalName || null);
  formData.append("web_site", values.webSite || "");
  appendIfNotEmpty(formData, "time_zone", values.timeZone);
  formData.append("po_box", values.poBox);
  // formData.append("dha_moh_no", values.dha_moh_no || 0);
  appendIfNotEmpty(formData, "enable_late_deduction", values.enableLateDeduction ? "yes" : "no");
  appendIfNotEmpty(formData, "late_deduction_type", values.lateDeductionType);
  appendIfNotEmpty(formData, "late_deduction_after_minutes", values.lateDeductionAfterMinutes);
  appendIfNotEmpty(formData, "late_deduction_leave_type", values.lateDeductionLeaveType);
  appendIfNotEmpty(formData, "enable_unmarked_attendance_deduction", values.enableUnmarkedAttendanceDeduction ? "yes" : "no");
  appendIfNotEmpty(formData, "unmarked_attendance_deduction_type", values.unmarkedAttendanceDeductionType);
  appendIfNotEmpty(formData, "unmarked_attendance_tolerance_days", values.unmarkedAttendanceToleranceDays);
  appendIfNotEmpty(formData, "leave_backdate_days", values.leaveBackdateDays);
  appendIfNotEmpty(formData, "sick_leave_backdate_days", values.sickLeaveBackdateDays);
  appendIfNotEmpty(formData, "enable_batch_number", values.enableBatchNumber ? "yes" : "no");
  formData.append("annual_leave_type", values.annualLeaveType === "Calendar Days" ? 1 : 0);
  formData.append("annual_leave_days", cleanNum(values.annualLeaveDays, 0));
  formData.append("sick_leave_days", cleanNum(values.sickLeaveDays, 0));
  formData.append("flexi_leave_days", cleanNum(values.flexiLeaveDays, 0));
  formData.append("should_reset_accrued_leave", values.shouldResetAccruedLeave ? "yes" : "no");
  formData.append("leaves_carry_forward", cleanNum(values.leavesCarryForward, 0));
  appendIfNotEmpty(formData, "auto_checkout_duration_minutes", values.autoCheckoutDurationMinutes);
  appendIfNotEmpty(
    formData,
    "weekend_days",
    Array.isArray(values.weekendDays)
      ? values.weekendDays.join(",")
      : (values.weekendDays || "")
  );
  formData.append("thermal_print", values.thermalPrint ? "yes" : "no");
  formData.append("print_type", clean(values.invoicePrintType, 10) || "a4");
  formData.append("is_warehouse", values.isWarehouse ? "yes" : "no");
  formData.append("is_cal_type", values.isCalType || "on_total");
  formData.append("is_auto_challan", values.isAutoChallan ? "yes" : "no");
  formData.append("is_voucher_receipt", values.isVoucherReceipt ? "yes" : "no");
  formData.append("is_minus_stock_sale", values.isMinusStockSale ? "yes" : "no");
  formData.append("allow_product_rate_change", values.allowProductRateChange ? "yes" : "no");
  formData.append("enable_cross_branch_item_sync", values.enableCrossBranchItemSync ? "yes" : "no");
  formData.append("is_serial", values.isSerial ? "yes" : "no");
  formData.append("apply_discount_to_avg_price", values.applyDiscountToAvgPrice ? "yes" : "no");
  // add alongside other fields
  formData.append("vat_stagger_month", values.vatStaggerMonth || "");
  formData.append(
    "invoice_start_number",
    values.invoiceStartNumber || 1
  );
  formData.append(
    "vat_deadline_day",
    values.vatDeadlineDay || 0
  );
  appendIfNotEmpty(formData, "customer_code_prefix", clean(values.customerCodePrefix, 20));

  appendIfNotEmpty(formData, "return_policy", cleanNum(values.branchReturnPolicy, 0));
  appendIfNotEmpty(formData, "replace_policy", cleanNum(values.branchReplacePolicy, 0));

  formData.append("quotation_terms", values.quotationTerms);
  formData.append("quotation_payment_terms", values.quotationPaymentTerms);
  formData.append("purchase_order_payment_terms", values.purchaseOrderPaymentTerms);

  // Design A print layout -- see backend/src/pdfTemplates/designA/. Bank
  // details for it come from the bank account marked primary in Chart of
  // Accounts (accounts.is_primary_bank_account), not from a field here.
  appendIfNotEmpty(formData, "print_template", values.printTemplate);
  formData.append("invoice_default_terms", values.invoiceDefaultTerms || "");

  if (values.branchLogo && values.branchLogo[0]?.originFileObj)
    formData.append("logo", values.branchLogo[0].originFileObj);
  if (values.branchStamp && values.branchStamp[0]?.originFileObj)
    formData.append("stamp_file", values.branchStamp[0].originFileObj);
  if (values.branchSignature && values.branchSignature[0]?.originFileObj)
    formData.append("signature_file", values.branchSignature[0].originFileObj);

  return formData;
};


// 🔹 Fetch Institution with counts + details
export const fetchInstitution = createAsyncThunk(
  "institution/fetchInstitution",
  async (_, { rejectWithValue, getState }) => {
    try {
      const state = getState();
      const authId = state.auth?.auth_info?.id;

      const [instRes, singleRes, countsRes] = await Promise.all([
        getInstitutionApi(),
        authId ? singleInstitutionDetailApi({ id: authId }) : Promise.resolve({ data: {} }),
        authId ? getCountDetailsApi({ institution_id: authId }) : Promise.resolve({ data: {} }),
      ]);

      const data = instRes.data || {};
      const single = singleRes.data?.data?.[0] || {};
      const counts = countsRes.data?.data || {};

      return {
        // Company
        id: data.id,
        companyName: data.name || "",
        legalName: single.company_title || "",
        einSsn: data.ein_ssn || "",
        taxForm: data.tax_form || "",
        subscriptionType: data.subscription_type || "",
        subscriptionExpiry: safeDate(data.subscription_expiry),
        companyType: data.company_type || single.company_type || "",
        country: single.country || data.country || "",
        countryCode: single.country_code || data.country_code || "",
        fy_start_month: data.fy_start_month || 1,
        migrationDate: data.migration_date || single.migration_date || null,
        vat_stagger_month: data.vat_stagger_month || null,
        vat_deadline_day: data.vat_deadline_day || null,
        // Contact
        companyEmail: data.company_email || "",
        phoneNo: data.phone_no || "",
        webSite: data.web_site || "",
        timeZone: single.time_zone || data.time_zone || "",
        thermalPrint: data.thermal_print === "yes",
        // Address
        streetAddress: single.street_address || "",
        poBox: data.po_box || single.po_box || "",

        // Finance
        currency: data.currency || "",
        vatPercent: Number(data.item_tax) || 0,
        vatLicense: data.vat_license || "",
        tradeLicenseNo: data.trade_license_no || "",
        tradeLicenseExpiry: safeDate(data.trade_license_expiry),
        dhaMohNo: data.dha_moh_no || "",
        dhaMohExpiry: safeDate(data.dha_moh_expiry),
        glnNo: data.gln_no || "",
        glnExpiry: safeDate(data.gln_no_expiry),
        description: data.description || "",
        customerCodePrefix: data.customer_code_prefix || "CMR",

        // HR
        enableLateDeduction: data.enable_late_deduction === "yes",
        lateDeductionType: data.late_deduction_type,
        lateDeductionAfterMinutes: data.late_deduction_after_minutes,
        lateDeductionLeaveType: data.late_deduction_leave_type || "casual_leave",
        enableUnmarkedAttendanceDeduction: data.enable_unmarked_attendance_deduction === "yes",
        unmarkedAttendanceDeductionType: data.unmarked_attendance_deduction_type || "casual_leave",
        unmarkedAttendanceToleranceDays: data.unmarked_attendance_tolerance_days ?? 0,
        leaveBackdateDays: data.leave_backdate_days ?? 7,
        sickLeaveBackdateDays: data.sick_leave_backdate_days ?? 7,
        enableBatchNumber: data.enable_batch_number !== "no",
        annualLeaveType: data.annual_leave_type === 1 ? "Calendar Days" : "Working Days",
        annualLeaveDays: Number(data.annual_leave_days) || 0,
        sickLeaveDays: Number(data.sick_leave_days) || 0,
        flexiLeaveDays: Number(data.flexi_leave_days) || 0,
        weekendDays: data.weekend_days || "",
        shouldResetAccruedLeave: data.should_reset_accrued_leave === "yes",
        leavesCarryForward: Number(data.leaves_carry_forward) || 0,
        autoCheckoutDurationMinutes: Number(data.auto_checkout_duration_minutes) || null,

        // Billing
        branchReturnPolicy: Number(data.return_policy) || 0,
        branchReplacePolicy: Number(data.replace_policy) || 0,
        invoicePrintType: data.print_type || "a4",
        isWarehouse: data.is_warehouse === "yes",
        isCalType: data.is_cal_type || "on_total",
        isAutoChallan: data.is_auto_challan === "yes",
        isVoucherReceipt: data.is_voucher_receipt === "yes",
        isMinusStockSale: data.is_minus_stock_sale === "yes",
        allowProductRateChange: data.allow_product_rate_change === "yes",
        enableCrossBranchItemSync: data.enable_cross_branch_item_sync === "yes",
        isSerial: data.is_serial === "yes",
        applyDiscountToAvgPrice: data.apply_discount_to_avg_price === "yes",
        quotationTerms: data.quotation_terms || "",
        quotationPaymentTerms: data.quotation_payment_terms || "",
        purchaseOrderPaymentTerms: data.purchase_order_payment_terms || "",

        // Design A print layout -- see backend/src/pdfTemplates/designA/.
        printTemplate: data.print_template || "design_a",
        invoiceDefaultTerms: data.invoice_default_terms || "",

        // Files
        branchLogo: data.logo || null,
        branchStamp: data.stamp || null,
        branchSignature: data.signature || null,

        // Counts
        usedBranchCount: counts?.branchCounts?.[0]?.used_branch_count ?? 0,
        totalBranchCount: single?.total_branch_count ?? 0,
        usedUserCount: counts?.userCounts?.[0]?.used_user_count ?? 0,
        totalUserCount: single?.total_user_count ?? 0,
        usedBasicCount: counts?.baseCounts?.[0]?.used_basic_count ?? 0,
        totalBasicCount: single?.basic_user_limit ?? 0,
        usedCoreCount: counts?.coreCounts?.[0]?.used_core_count ?? 0,
        totalCoreCount: single?.core_user_limit ?? 0,
        onboarding_status: data.onboarding_status || "pending",
        invoiceStartNumber: single.invoice_start_number || 1,
      };
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Failed to fetch institution");

    }
  }
);

// 🔹 Update Institution
export const updateInstitution = createAsyncThunk(
  "institution/updateInstitution",
  async ({ values }, { rejectWithValue }) => {
    try {
      const formData = buildInstitutionFormData(values);

      const res = await saveInstitutionApi(formData);

      if (res.data?.error) {

        return rejectWithValue(res.data.message || "Failed to update institution");
      }
      return res.data;
    } catch (err) {

      return rejectWithValue(err.response?.data?.message || err?.message || "Failed to update institution");
    }
  }
);


export const updateOnboardingStatus = createAsyncThunk(
  "institution/updateOnboardingStatus",
  async (status, { rejectWithValue }) => {
    try {
      await updateOnboardingStatusApi({
        onboarding_status: status,
      });
      return status;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);
